<template>

    <Toast/>

    <MenuFinancialCompact v-if="userStore.user?.roles?.includes('ROLE_ISORISE')" />
    <MenuHealthCompact v-else />

    <div class="px-6 surface-overlay relative">

        <div v-show="activeTab === 1" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <ul class="list-none py-2 px-0 m-0 flex align-items-center font-medium overflow-x-auto w-full">
                        <li class="pr-3"><a class="cursor-pointer">Configuration</a></li>
                        <li class="px-2"><i class="pi pi-angle-right text-900"></i></li>
                        <li class="px-2"><a class="cursor-pointer text-900 white-space-nowrap">Team</a></li>
                    </ul>

                    <h3>Manage Team</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callNewTeamMember" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>New Team Member</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <DataTable
                        :value="team"
                        :paginator="true"
                        class="p-datatable-gridlines"
                        :rows="100"
                        dataKey="id"
                        :rowsPerPageOptions="[10,20,50,100]"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        :sortMode="'multiple'"
                        v-model:filters="filters"
                        filterDisplay="menu"
                        :loading="loading"
                        responsiveLayout="scroll"
                        :globalFilterFields="['username', 'firstName', 'lastName', 'email', 'mobile']">

                        <template #header>
                            <div class="p-grid">
                                <div class="p-col-12 p-md-2">
                                    <span class="p-input-icon-left">
                                        <i class="pi pi-search"/>
                                        <InputText v-model="filters['global'].value"
                                                   placeholder="Keyword Search on Username, First Name, Last Name, Email or Mobile"
                                                   autocomplete="off"
                                                   name="team_search"
                                                   readonly
                                                   @focus="$event.target.removeAttribute('readonly')"
                                                   style="width: 100%"/>
                                    </span>
                                </div>
                            </div>

                        </template>

                        <template #empty>
                            No team found.
                        </template>

                        <template #loading>
                            Loading team. Please wait.
                        </template>

                        <Column
                            field="selectAnOption"
                            header=""
                            :headerStyle="{ width: '2rem' }"
                            :bodyStyle="{ width: '2rem', padding: '0.25rem' }">
                            <template #body="{ data, index }">
                                <Button icon="pi pi-ellipsis-v" class="p-button-sm p-button-text p-button-rounded m-0"
                                        @click="$refs['menu' + index].toggle($event)"/>
                                <Menu
                                    :ref="'menu' + index"
                                    :model="getFilteredItems(data)"
                                    :popup="true"/>
                            </template>
                        </Column>

                        <Column field="firstName" :sortable="true">
                            <template #header>
                                <div class="flex justify-content-center align-items-center w-full">
                                    First Name
                                </div>
                            </template>

                            <template #body="{ data }">
                                <div class="flex justify-content-center align-items-center w-full">
                                    {{ data.firstName }}
                                </div>
                            </template>
                        </Column>

                        <Column field="lastName" :sortable="true">
                            <template #header>
                                <div class="flex justify-content-center align-items-center w-full">
                                    Last Name
                                </div>
                            </template>

                            <template #body="{ data }">
                                <div class="flex justify-content-center align-items-center w-full">
                                    {{ data.lastName }}
                                </div>
                            </template>
                        </Column>

                        <Column field="username" :sortable="true">
                            <template #header>
                                <div class="flex justify-content-center align-items-center w-full">
                                    Username
                                </div>
                            </template>

                            <template #body="{ data }">
                                <div class="flex justify-content-center align-items-center w-full">
                                    {{ data.username }}
                                </div>
                            </template>
                        </Column>

                        <Column field="email" :sortable="true">
                            <template #header>
                                <div class="flex justify-content-center align-items-center w-full">
                                    Email
                                </div>
                            </template>

                            <template #body="{ data }">
                                <div class="flex justify-content-center align-items-center w-full">
                                    {{ data.email }}
                                </div>
                            </template>
                        </Column>

                        <Column field="mobile" :sortable="true">
                            <template #header>
                                <div class="flex justify-content-center align-items-center w-full">
                                    Mobile
                                </div>
                            </template>

                            <template #body="{ data }">
                                <div class="flex justify-content-center align-items-center w-full">
                                    {{ data.mobile }}
                                </div>
                            </template>
                        </Column>

                        <Column field="rights" :sortable="true">
                            <template #header>
                                <div class="flex justify-content-center align-items-center w-full">
                                    Enable TFA
                                </div>
                            </template>

                            <template #body="{ data }">
                                <div class="flex justify-content-center align-items-center w-full">
                                    <span v-if="data.rights === 'A'"
                                          :class="'status-badge status-' + data.rights">Admin</span>
                                    <span v-if="data.rights === 'M'"
                                          :class="'status-badge status-' + data.rights">Individual View</span>
                                </div>
                            </template>
                        </Column>

                        <Column field="enableTfa" :sortable="true">
                            <template #header>
                                <div class="flex justify-content-center align-items-center w-full">
                                    Enable TFA
                                </div>
                            </template>

                            <template #body="{ data }">
                                <div class="flex justify-content-center align-items-center w-full">
                                    <span v-if="data.enableTfa === 'Y'"
                                          :class="'status-badge status-' + data.enableTfa">Yes</span>
                                    <span v-if="data.enableTfa === 'N'"
                                          :class="'status-badge status-' + data.enableTfa">No</span>
                                </div>
                            </template>
                        </Column>

                        <Column field="status" :sortable="true">
                            <template #header>
                                <div class="flex justify-content-center align-items-center w-full">
                                    Status
                                </div>
                            </template>

                            <template #body="{ data }">
                                <div class="flex justify-content-center align-items-center w-full">
                                    <span v-if="data.status === 'A'"
                                          :class="'status-badge status-' + data.status">Active</span>
                                    <span v-if="data.status === 'I'"
                                          :class="'status-badge status-' + data.status">Inactive</span>
                                </div>
                            </template>
                        </Column>

                    </DataTable>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 2" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Update Team Member {{ this.teamMember.firstName }} {{ this.teamMember.lastName }}</h3>

                    <div class="field">
                        <label for="firstName">First Name</label>
                        <InputText id="firstName" v-model.trim="teamMember.firstName" required="true"/>
                    </div>

                    <div class="field">
                        <label for="lastName">Last Name</label>
                        <InputText id="lastName" v-model.trim="teamMember.lastName" required="true"/>
                    </div>

                    <div class="field">
                        <label for="Username">Username</label>
                        <InputText id="Username" v-model.trim="teamMember.username" required="true"/>
                    </div>

                    <div class="field">
                        <label for="email">Email</label>
                        <InputText id="email" v-model.trim="teamMember.email" required="true"/>
                    </div>

                    <div class="field">
                        <label for="mobile">mobile</label>
                        <InputText id="mobile" v-model.trim="teamMember.mobile" required="true"/>
                    </div>

                    <div class="field">
                        <label for="password1">New Password</label>
                        <InputText id="password1" type="password" v-model.trim="teamMember.password1"/>
                    </div>

                    <div class="field">
                        <label for="password2">Verify Password</label>
                        <InputText id="password2" type="password" v-model.trim="teamMember.password2"/>
                    </div>

                    <div class="field">
                        <label for="rights">Rights</label>
                        <Dropdown v-model="teamMember.rights" :options="rightsOption" optionLabel="name"
                                  optionValue="code" placeholder="Select a Right"/>
                    </div>

                    <div class="field">
                        <label for="status">Status</label>
                        <Dropdown v-model="teamMember.status" :options="statusTypes" optionLabel="name"
                                  optionValue="code" placeholder="Select a Status"/>
                    </div>

                    <Button label="Update" class="mr-2 mb-2" @click="updateTeamMember"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 3" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Delete Team Member {{ this.teamMember.firstName }} {{ this.teamMember.lastName }}</h3>

                    <div class="field">
                        <label for="deleteConfirmationResponse">Do You Wish to Delete this Team Member?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Delete" class="mr-2 mb-2" @click="deleteTeamMember"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 4" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Create Team Member</h3>

                    <div class="field">
                        <label for="firstName">First Name</label>
                        <InputText id="firstName" v-model.trim="newTeamMember.firstName" required="true"/>
                    </div>

                    <div class="field">
                        <label for="lastName">Last Name</label>
                        <InputText id="lastName" v-model.trim="newTeamMember.lastName" required="true"/>
                    </div>

                    <div class="field">
                        <label for="Username">Username</label>
                        <InputText id="Username" v-model.trim="newTeamMember.username" required="true"/>
                    </div>

                    <div class="field">
                        <label for="email">Email</label>
                        <InputText id="email" v-model.trim="newTeamMember.email" required="true"/>
                    </div>

                    <div class="field">
                        <label for="mobile">Mobile</label>
                        <InputText id="mobile" v-model.trim="newTeamMember.mobile" required="true"/>
                    </div>

                    <div class="field">
                        <label for="password1">New Password</label>
                        <InputText id="password1" type="password" v-model.trim="newTeamMember.password1"/>
                    </div>

                    <div class="field">
                        <label for="password2">Verify Password</label>
                        <InputText id="password2" type="password" v-model.trim="newTeamMember.password2"/>
                    </div>

                    <div class="field">
                        <label for="rights">Rights</label>
                        <Dropdown v-model="newTeamMember.rights" :options="rightsOption" optionLabel="name"
                                  optionValue="code" placeholder="Select a Right"/>
                    </div>


                    <Button label="Create" class="mr-2 mb-2" @click="createTeamMember"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

    </div>

    <IsoEvolveFooter/>

</template>

<script>
import IsoEvolveFooter from "@/components/heading/authentication/IsoEvolveFooter.vue";
import MenuHealthCompact from "@/components/menu/health/MenuHealthCompact.vue";
import {useUserStore} from "@/stores/UserStore";
import UserService from "@/service/UserService";
import CompanyService from "@/service/CompanyService";
import {FilterMatchMode, FilterOperator} from "primevue/api";
import TeamService from "@/service/TeamService";
import MenuFinancialCompact from "@/components/menu/famodule/MenuFinancialCompact.vue";

export default {
    components: {
        MenuFinancialCompact,
        IsoEvolveFooter, MenuHealthCompact
    },
    setup() {
        const userStore = useUserStore()

        return {
            userStore
        }
    },
    created() {
        this.tableKey++;  // forces DataTable to remount fresh
        this.initFilters();

        this.standardFilters = [
            {field: 'active', header: 'Active'},
            {field: 'inactive', header: 'Inactive'}
        ];

    },
    activated() {
        this.initFilters();
    },
    async mounted() {
        await this.loadCompanyContext();
    },
    data() {

        return {
            profile: {},
            activeTab: 1,
            companyConfiguration: {},
            userConfiguration: {},
            filters: {},
            loading: true,
            submitted: false,
            filteredItemsCache: new Map(),
            teamMember: {},
            newTeamMember: {},
            team: [],
            originalTeam: [],
            confirmationResponse: 'N',
            selectedColumns: null,
            columnsDefault: null,
            columnsFullList: null,
            selectedStandardFilter: null,
            standardFilters: null,
            confirmationTypes: [
                {name: 'Yes', code: 'Y'},
                {name: 'No', code: 'N'}
            ],
            statusTypes: [
                {name: 'Active', code: 'A'},
                {name: 'Inactive', code: 'I'}
            ],
            rightsOption: [
                {name: 'Admin', code: 'A'},
                {name: 'Individual View', code: 'M'}
            ],
        }
    },
    watch: {
        'userStore.activeCompanyId': {
            immediate: false,
            async handler(newCompanyId, oldCompanyId) {
                if (!newCompanyId || newCompanyId === oldCompanyId) {
                    return;
                }

                console.log(
                    '[Appointments][watch] Company changed:',
                    oldCompanyId,
                    '→',
                    newCompanyId
                );

                await this.loadCompanyContext();
            }
        }
    },
    methods: {
        async loadCompanyContext() {
            try {
                // Attempt to get user data
                const userData = await UserService.getUser(this.userStore.user.id);
                this.profile = userData.data;
            } catch (e) {
                console.error("[AppointmentsNew][mounted] Error fetching user data = ", e);
                this.userStore.clearUser();
                this.$router.push('login');
                return;
            }

            this.profile.companyId = this.userStore.activeCompanyId;

            try {
                const [userConfigData] = await Promise.all([
                    UserService.getUserConfig(this.userStore.user.id)
                ]);

                this.userConfiguration = userConfigData.data;
                console.log("[ConfigurationTeam][mounted] userConfigData.data = ", userConfigData.data);
            } catch (e) {
                console.error("[ConfigurationTeam][mounted] Could not find user config for user = ", this.userStore.user.id);
            }

            try {
                const [teamData, companyConfigData] = await Promise.all([
                    TeamService.getTeamForCompany(this.profile.companyId),
                    CompanyService.getCompanyConfiguration(this.profile.companyId)
                ]);

                this.team = teamData.data;
                this.originalTeam = teamData.data;
                console.log("[ConfigurationTeam][mounted] teamData.data = ", teamData.data);

                this.companyConfiguration = companyConfigData.data;
                console.log("[ConfigurationTeam][mounted] companyConfigData.data = ", companyConfigData.data);

                this.columnsDefault = [
                    {field: 'firstName', header: 'First Name', sortable: true},
                    {field: 'lastName', header: 'Last Name', sortable: true},
                    {field: 'username', header: 'Username', sortable: true},
                    {field: 'email', header: 'Email', sortable: true},
                    {field: 'mobile', header: 'Mobile', sortable: true},
                    {field: 'enableTfa', header: 'Enable TFA', sortable: true},
                    {field: 'status', header: 'Status', sortable: true}
                ];

                this.columnsFullList = [
                    {field: 'firstName', header: 'First Name', sortable: true},
                    {field: 'lastName', header: 'Last Name', sortable: true},
                    {field: 'username', header: 'Username', sortable: true},
                    {field: 'email', header: 'Email', sortable: true},
                    {field: 'mobile', header: 'Mobile', sortable: true},
                    {field: 'created', header: 'Created', sortable: true},
                    {field: 'enableTfa', header: 'Enable TFA', sortable: true},
                    {field: 'status', header: 'Status', sortable: true}
                ];

                this.selectedColumns = this.columnsDefault;

                this.loading = false;

            } catch (e) {
                console.error("[ConfigurationTeam][mounted] Error fetching additional data = ", e);
            }

            console.log("[ConfigurationTeam][mounted] this.team = ", this.team);
            console.log("[ConfigurationTeam][mounted] this.userConfiguration = ", this.userConfiguration);
            console.log("[ConfigurationTeam][mounted] this.companyConfiguration = ", this.companyConfiguration);
        },
        initFilters() {
            this.filters = {
                'global': {value: null, matchMode: FilterMatchMode.CONTAINS},
                'firstName': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'lastName': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'username': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'email': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'mobile': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                }
            }
        },
        clearFilter() {
            this.initFilters();
        },
        restoreToOriginal() {
            window.location.reload();
        },
        callReset() {
            this.activeTab = 1;
        },
        callNewTeamMember() {
            this.newTeamMember = {};
            this.newTeamMember.rights = 'A';
            this.newTeamMember.status = 'A';
            this.newTeamMember.enableTfa = 'Y';
            this.activeTab = 4;
        },
        callUpdateTeamMember(data) {
            this.activeTab = 2;
            this.teamMember = data;
        },
        callDeleteTeamMember(data) {
            this.activeTab = 3;
            this.confirmationResponse = 'N';
            this.teamMember = data;
        },
        onToggleColumns(value) {
            this.selectedColumns = this.columnsFullList.filter(col => value.includes(col));
        },
        onToggleStandardFilters(value) {

            if (value.length > 1) {
                // Keep the last selected option
                this.selectedStandardFilter = [value[value.length - 1]];
            } else {
                this.selectedStandardFilter = value;
            }

            const isActive = value.some(item => item.field === "active");
            const isInactive = value.some(item => item.field === "inactive");

            if (this.team !== null) {

                if (isActive) {
                    this.team = this.originalTeam.filter(teamMember => teamMember.status === 'A');
                } else if (isInactive) {
                    this.team = this.originalTeam.filter(teamMember => teamMember.status === 'I');
                } else {
                    this.team = this.originalTeam;
                }

            } else {
                // Handle the case when auditTrails is null
                this.team = null;
            }
        },
        getFilteredItems(data) {
            if (this.filteredItemsCache.has(data.id)) {
                return this.filteredItemsCache.get(data.id);
            }

            const items = [];

            items.push({
                label: 'Update',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callUpdateTeamMember(data);
                }
            });

            items.push({
                label: 'Delete',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callDeleteTeamMember(data);
                }
            });

            this.filteredItemsCache.set(data.id, items);

            return items;
        },
        chooseBlankSelection(data) {
            console.log('[ConfigurationTeam][chooseBlankSelection] data = ', data);
            this.$toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Please select a valid option.',
                life: 3000
            });
        },
        async createTeamMember() {
            this.submitted = true;

            if (this.newTeamMember.firstName) {

                if (this.newTeamMember.password1.trim()) {
                    if (this.newTeamMember.password1.length < 8) {
                        console.log("[Profile][processUpdatePassword] Got into password length check. Password1 = ", this.newTeamMember.password1);
                        this.$toast.add({
                            severity: 'error',
                            summary: 'Unsuccessful',
                            detail: 'Password needs to be at least 8 characters',
                            life: 3000
                        });
                    } else if (this.newTeamMember.password1 !== this.newTeamMember.password2) {
                        this.$toast.add({severity: 'error', summary: 'Unsuccessful', detail: 'Passwords do not match', life: 3000});
                    } else if (this.newTeamMember.password1 === this.newTeamMember.password2) {
                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Passwords do match',
                            life: 3000
                        });

                        try {
                            var teamMemberDto = {
                                firstName: this.newTeamMember.firstName,
                                lastName: this.newTeamMember.lastName,
                                username: this.newTeamMember.username,
                                email: this.newTeamMember.email,
                                mobile: this.newTeamMember.mobile,
                                enableTfa: this.newTeamMember.enableTfa,
                                password: this.newTeamMember.password1,
                                rights: this.newTeamMember.rights,
                                companyId: this.profile.companyId,
                                status: 'A'
                            };

                            const response = await TeamService.createTeamMember(this.profile.companyId, teamMemberDto);
                            console.log('[ConfigurationTeam][createTeamMember] Response = ', response);

                            const [teamData] = await Promise.all([
                                TeamService.getTeamForCompany(this.profile.companyId)
                            ]);

                            this.team = teamData.data;
                            this.originalTeam = teamData.data;

                            this.$toast.add({
                                severity: 'success',
                                summary: 'Successful',
                                detail: 'Created team member',
                                life: 3000
                            });

                        } catch (error) {
                            console.error('[ConfigurationTeam][createTeamMember] Error:', error);

                            this.$toast.add({
                                severity: 'error',
                                summary: 'Error',
                                detail: 'Failed to create team member. Please try again.',
                                life: 3000
                            });
                        }
                    } else {
                        this.$toast.add({severity: 'error', summary: 'Unsuccessful', detail: 'Could not create new team member', life: 3000});
                    }
                }
            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async updateTeamMember() {
            this.submitted = true;

            if (this.teamMember.id) {

                try {
                    var teamMemberDto = {
                        id: this.teamMember.id,
                        firstName: this.teamMember.firstName,
                        lastName: this.teamMember.lastName,
                        username: this.teamMember.username,
                        email: this.teamMember.email,
                        mobile: this.teamMember.mobile,
                        enableTfa: this.teamMember.enableTfa,
                        rights: this.teamMember.rights,
                        companyId: this.teamMember.companyId,
                        status: this.teamMember.status,
                    };

                    if (this.teamMember.password1 === this.teamMember.password2) {
                        teamMemberDto.password = this.teamMember.password1;
                    }

                    const response = await TeamService.updateTeamMember(this.teamMember.id, teamMemberDto);
                    console.log('[ConfigurationTeam][updateTeamMember] Response = ', response);

                    const [teamData] = await Promise.all([
                        TeamService.getTeamForCompany(this.profile.companyId)
                    ]);

                    this.team = teamData.data;
                    this.originalTeam = teamData.data;

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Updated team member',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[ConfigurationTeam][updateTeamMember] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to update team member. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async deleteTeamMember() {
            this.submitted = true;

            if (this.confirmationResponse.trim() === "Y") {

                if (this.teamMember.id) {

                    try {
                        const response = await TeamService.deleteTeamMember(this.teamMember.id);
                        console.log('[ConfigurationTeam][deleteTeamMember] Response = ', response);

                        const [teamData] = await Promise.all([
                            TeamService.getTeamForCompany(this.profile.companyId)
                        ]);

                        this.team = teamData.data;
                        this.originalTeam = teamData.data;

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Deleted team member',
                            life: 3000
                        });

                    } catch (error) {
                        console.error('[ConfigurationTeam][deleteTeamMember] Error:', error);

                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to delete team member. Please try again.',
                            life: 3000
                        });
                    }
                }

            }

            this.activeTab = 1;
            this.submitted = false;
        }
    },
};
</script>

<style scoped lang="scss">
a {
    text-decoration: none;
}

::v-deep(.p-datatable-frozen-tbody) {
    font-weight: bold;
}

::v-deep(.p-datatable-scrollable .p-frozen-column) {
    font-weight: bold;
}

::v-deep(.p-progressbar) {
    height: .5rem;
    background-color: #D8DADC;

    .p-progressbar-value {
        background-color: #607D8B;
    }
}

.status-badge, .type-badge {
    border-radius: var(--border-radius);
    padding: .25em .5rem;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: .3px;
}

.status-badge {
    &.status-A {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-I {
        background: #B3E5FC;
        color: #23547B;
    }

    &.status-Y {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-N {
        background: #B3E5FC;
        color: #23547B;
    }

    &.status-M {
        background: #B3E5FC;
        color: #23547B;
    }

}

.type-badge {
    &.status-C {
        background: #FEEDAF;
        color: #8A5340;
    }

    &.status-S {
        background: #B3E5FC;
        color: #23547B;
    }

    &.status-I {
        background: #bc91d5;
        color: #71038f;
    }

}

.tabview-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-tabview p {
    line-height: 1.5;
    margin: 0;
}

.accordion-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-accordion p {
    line-height: 1.5;
    margin: 0;
}

.mb-2 {
    margin-bottom: 1rem;
}

.mr-2 {
    margin-right: 1rem;
}

</style>
